import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ChevronRight } from 'lucide-react';
import { SingleSelectDropdown } from './ui/SingleSelectDropdown';
import { MultiSelectDropdown } from './ui/MultiSelectDropdown';
interface SurveyData {
  // Welcome
  email: string;
  // Your Investing Background
  yearsInvesting: string;
  startupsInvested: string;
  typicalInvestmentAmount: string;
  investmentStages: string[];
  howYouInvest: string;
  howYouInvestOther: string;
  geographiesMarkets: string;
  industries: string[];
  industriesOther: string;
  // Portfolio Composition
  soloVsCofounderPercentage: string;
  personalPreference: string;
  preferCofounderReasons: string[];
  preferCofounderReasonsOther: string;
  preferSoloReasons: string[];
  preferSoloReasonsOther: string;
  viewsChanged: string;
  viewsChangedExplanation: string;
  // Evaluating Founding Teams
  teamFoundersImportance: string;
  marketOpportunityImportance: string;
  productTechnologyImportance: string;
  tractionMetricsImportance: string;
  cofounderDynamicsImportance: string;
  alignmentMattersMost: string;
  timeAssessingDynamics: string;
  // Signals of Healthy Relationships
  healthySignals: string[];
  healthySignalsOther: string;
  singleMostImportantIndicator: string;
  questionsToAssess: string;
  observeInteractions: string;
  specificBehaviors: string[];
  specificBehaviorsOther: string;
  changedDecision: string;
  observationExample: string;
  // Red Flags and Warning Signs
  redFlags: string[];
  redFlagsOther: string;
  singleBiggestRedFlag: string;
  investedDespiteRedFlags: string;
  investedDespiteRedFlagsOutcome: string;
  avoidedDueToConflict: string;
  avoidedDueToConflictDetails: string;
  // Cofounder Conflict
  portfolioConflictPercentage: string;
  conflictTriggers: string[];
  conflictTriggersOther: string;
  conflictStage: string;
  unresolvedTensionTimeline: string;
  investorApproach: string;
  recoverVsDontDifferentiator: string;
  helpfulResources: string[];
  helpfulResourcesOther: string;
  // Cofounder Splits and Departures
  cofounderSplitPercentage: string;
  splitStage: string;
  departureOutcome: string;
  survivalFactors: string[];
  survivalFactorsOther: string;
  survivedSplitStory: string;
  failedSplitStory: string;
  derailedExit: string;
  derailedExitDetails: string;
  alignmentAffectValuation: number;
  // Predicting Long-Term Alignment
  predictFromEarly: string;
  earlyPositiveIndicators: string[];
  earlyPositiveIndicatorsOther: string;
  earlyNegativeIndicators: string[];
  earlyNegativeIndicatorsOther: string;
  underratedFactor: string;
  // Solo Founders
  soloFounderQualities: string[];
  soloFounderQualitiesOther: string;
  singleMostImportantQuality: string;
  soloFounderRisks: string[];
  soloFounderRisksOther: string;
  soloFounderMitigation: string;
  soloOutperformCofounder: string;
  soloExitLikelihood: string;
  // Founder Growth and Evolution
  dynamicsImproveFrequency: string;
  growTogetherBehaviors: string[];
  growTogetherBehaviorsOther: string;
  misalignmentCorrectable: string;
  spotGrowthPotential: string;
  // Investor's Role in Alignment
  investorResponsibility: number;
  investorInfluence: string;
  supportHealthyDynamics: string[];
  supportHealthyDynamicsOther: string;
  investorMistakes: string;
  wishFoundersUnderstood: string;
  // Future of Founder Fit Assessment
  founderFitFormalDueDiligence: string;
  useCofounderFitScore: string;
  alignmentScoreInfluence: number;
  toolCredibility: string[];
  toolCredibilityOther: string;
  toolDimensions: string[];
  toolDimensionsOther: string;
  futureAssessment: string;
  // Stories and Lessons Learned
  mostTeachingStory: string;
  strongDynamicsExample: string;
  misalignmentExample: string;
  bestCofounderRelationship: string;
  worstCofounderBreakdown: string;
  // Advice for Founders and Investors
  adviceToCofounders: string;
  adviceOnTrust: string;
  adviceToNewInvestor: string;
  wishSomeoneToldYou: string;
  // Final Thoughts
  whyAlignmentMatters: string;
  anythingElse: string;
  openToFollowUp: string;
  mayQuote: string;
}
const SECTIONS = [{
  id: 'welcome',
  label: 'Welcome',
  required: false
}, {
  id: 'background',
  label: 'Your Background',
  required: true
}, {
  id: 'portfolio',
  label: 'Portfolio',
  required: true
}, {
  id: 'evaluating',
  label: 'Evaluating Teams',
  required: false
}, {
  id: 'signals',
  label: 'Healthy Signals',
  required: false
}, {
  id: 'red-flags',
  label: 'Red Flags',
  required: false
}, {
  id: 'conflict',
  label: 'Conflict',
  required: false
}, {
  id: 'splits',
  label: 'Splits & Departures',
  required: false
}, {
  id: 'predicting',
  label: 'Predicting Alignment',
  required: false
}, {
  id: 'solo',
  label: 'Solo Founders',
  required: false
}, {
  id: 'growth',
  label: 'Growth & Evolution',
  required: false
}, {
  id: 'investor-role',
  label: 'Investor Role',
  required: false
}, {
  id: 'future',
  label: 'Future Assessment',
  required: false
}, {
  id: 'stories',
  label: 'Stories & Lessons',
  required: false
}, {
  id: 'advice',
  label: 'Advice',
  required: false
}, {
  id: 'final',
  label: 'Final Thoughts',
  required: false
}];
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxDrIiMDKHA0FCj1Y1BW2bKHaR5lFdfn7YKKx0xSPjbMXgQptCac1RmbcgCzGHYo_SSjQ/exec';
export function InvestorSurvey({
  onClose
}: {
  onClose: () => void;
}) {
  const [currentSection, setCurrentSection] = useState('welcome');
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SurveyData>({
    email: '',
    yearsInvesting: '',
    startupsInvested: '',
    typicalInvestmentAmount: '',
    investmentStages: [],
    howYouInvest: '',
    howYouInvestOther: '',
    geographiesMarkets: '',
    industries: [],
    industriesOther: '',
    soloVsCofounderPercentage: '',
    personalPreference: '',
    preferCofounderReasons: [],
    preferCofounderReasonsOther: '',
    preferSoloReasons: [],
    preferSoloReasonsOther: '',
    viewsChanged: '',
    viewsChangedExplanation: '',
    teamFoundersImportance: '',
    marketOpportunityImportance: '',
    productTechnologyImportance: '',
    tractionMetricsImportance: '',
    cofounderDynamicsImportance: '',
    alignmentMattersMost: '',
    timeAssessingDynamics: '',
    healthySignals: [],
    healthySignalsOther: '',
    singleMostImportantIndicator: '',
    questionsToAssess: '',
    observeInteractions: '',
    specificBehaviors: [],
    specificBehaviorsOther: '',
    changedDecision: '',
    observationExample: '',
    redFlags: [],
    redFlagsOther: '',
    singleBiggestRedFlag: '',
    investedDespiteRedFlags: '',
    investedDespiteRedFlagsOutcome: '',
    avoidedDueToConflict: '',
    avoidedDueToConflictDetails: '',
    portfolioConflictPercentage: '',
    conflictTriggers: [],
    conflictTriggersOther: '',
    conflictStage: '',
    unresolvedTensionTimeline: '',
    investorApproach: '',
    recoverVsDontDifferentiator: '',
    helpfulResources: [],
    helpfulResourcesOther: '',
    cofounderSplitPercentage: '',
    splitStage: '',
    departureOutcome: '',
    survivalFactors: [],
    survivalFactorsOther: '',
    survivedSplitStory: '',
    failedSplitStory: '',
    derailedExit: '',
    derailedExitDetails: '',
    alignmentAffectValuation: 0,
    predictFromEarly: '',
    earlyPositiveIndicators: [],
    earlyPositiveIndicatorsOther: '',
    earlyNegativeIndicators: [],
    earlyNegativeIndicatorsOther: '',
    underratedFactor: '',
    soloFounderQualities: [],
    soloFounderQualitiesOther: '',
    singleMostImportantQuality: '',
    soloFounderRisks: [],
    soloFounderRisksOther: '',
    soloFounderMitigation: '',
    soloOutperformCofounder: '',
    soloExitLikelihood: '',
    dynamicsImproveFrequency: '',
    growTogetherBehaviors: [],
    growTogetherBehaviorsOther: '',
    misalignmentCorrectable: '',
    spotGrowthPotential: '',
    investorResponsibility: 0,
    investorInfluence: '',
    supportHealthyDynamics: [],
    supportHealthyDynamicsOther: '',
    investorMistakes: '',
    wishFoundersUnderstood: '',
    founderFitFormalDueDiligence: '',
    useCofounderFitScore: '',
    alignmentScoreInfluence: 0,
    toolCredibility: [],
    toolCredibilityOther: '',
    toolDimensions: [],
    toolDimensionsOther: '',
    futureAssessment: '',
    mostTeachingStory: '',
    strongDynamicsExample: '',
    misalignmentExample: '',
    bestCofounderRelationship: '',
    worstCofounderBreakdown: '',
    adviceToCofounders: '',
    adviceOnTrust: '',
    adviceToNewInvestor: '',
    wishSomeoneToldYou: '',
    whyAlignmentMatters: '',
    anythingElse: '',
    openToFollowUp: '',
    mayQuote: ''
  });
  const [sessionId] = useState(() => `investor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isSaving, setIsSaving] = useState(false);
  const toggleArrayItem = (array: string[], item: string) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };
  const autoSaveProgress = async () => {
    setIsSaving(true);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection,
        isComplete: false,
        metadata
      };
      console.log('🔍 Auto-saving to:', GOOGLE_SCRIPT_URL);
      console.log('📦 Data being sent:', JSON.stringify(submissionData, null, 2));
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      console.log('✅ Progress auto-saved');
    } catch (error) {
      console.error('❌ Auto-save error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const markSectionComplete = (sectionId: string) => {
    setCompletedSections(prev => new Set([...prev, sectionId]));
  };
  const goToNextSection = async () => {
    markSectionComplete(currentSection);
    // Auto-save progress
    await autoSaveProgress();
    const currentIndex = SECTIONS.findIndex(s => s.id === currentSection);
    if (currentIndex < SECTIONS.length - 1) {
      setCurrentSection(SECTIONS[currentIndex + 1].id);
      window.scrollTo(0, 0);
    }
  };
  // NEW: Handle section tab clicks with auto-save
  const handleSectionChange = async (sectionId: string) => {
    // Save current progress before switching
    await autoSaveProgress();
    setCurrentSection(sectionId);
    window.scrollTo(0, 0);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection: 'final',
        isComplete: true,
        metadata
      };
      console.log('🚀 Submitting to:', GOOGLE_SCRIPT_URL);
      console.log('📝 Form data with metadata:', JSON.stringify(submissionData, null, 2));
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      console.log('✅ Submission successful!');
      setIsSubmitted(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('❌ Submission error:', error);
      setSubmitError('There was an error submitting your response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-349">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-[16px] p-12 max-w-md w-full text-center" data-id="element-350">
          <div className="w-16 h-16 bg-[#C5F467] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-351">
            <Check className="w-8 h-8 text-[#1A3D3A]" data-id="element-352" />
          </div>
          <h2 className="text-[#1A3D3A] mb-4" style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '32px',
          fontWeight: 500
        }} data-id="element-353">
            Thank You!
          </h2>
          <p className="text-[#3D5654] mb-8" style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '16px',
          lineHeight: 1.6
        }} data-id="element-354">
            Your insights are invaluable to understanding cofounder dynamics and
            helping founders build stronger partnerships.
          </p>
          <p className="text-[#3D5654] text-sm" style={{
          fontFamily: "'DM Sans', sans-serif",
          opacity: 0.7
        }} data-id="element-355">
            Redirecting you to the home page...
          </p>
        </motion.div>
      </div>;
  }
  const renderSection = () => {
    switch (currentSection) {
      case 'welcome':
        return <div className="space-y-8" data-id="element-356">
            <div data-id="element-357">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-358">
                Email Address
              </label>
              <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="your@email.com" data-id="element-359" />
            </div>

            <div className="bg-[#F8FAF9] p-6 rounded-lg" data-id="element-360">
              <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.7
            }} data-id="element-361">
                Thank you for participating in this research on cofounder
                dynamics and founder alignment. Your insights as an investor
                will help us understand what separates founding teams that
                thrive from those that fracture — and how alignment can be
                better assessed and supported. This survey takes approximately
                15-20 minutes. Your responses may inform future tools,
                resources, and a forthcoming book on founder partnerships.
              </p>
            </div>
          </div>;
      case 'background':
        return <div className="space-y-8" data-id="element-362">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-363">
              Your Investing Background
            </h3>

            <SingleSelectDropdown label="How many years have you been actively investing in startups?" options={['0–2 years', '3–5 years', '6–10 years', '10–20 years', 'More than 20 years']} selected={formData.yearsInvesting} onChange={selected => setFormData({
            ...formData,
            yearsInvesting: selected
          })} placeholder="Select years..." data-id="element-364" />

            <SingleSelectDropdown label="Approximately how many startups have you invested in to date?" options={['1–5', '6–15', '16–30', '31–50', '51–100', 'More than 100']} selected={formData.startupsInvested} onChange={selected => setFormData({
            ...formData,
            startupsInvested: selected
          })} placeholder="Select range..." data-id="element-365" />

            <SingleSelectDropdown label="What is your typical investment amount per deal?" options={['Less than $25K', '$25K–$50K', '$50K–$250K', '$250K–$1M', '$1M–$5M', 'More than $5M']} selected={formData.typicalInvestmentAmount} onChange={selected => setFormData({
            ...formData,
            typicalInvestmentAmount: selected
          })} placeholder="Select amount..." data-id="element-366" />

            <MultiSelectDropdown label="At what stage do you typically invest? (Select all that apply)" options={['Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Growth and Later Stage']} selected={formData.investmentStages} onChange={selected => setFormData({
            ...formData,
            investmentStages: selected
          })} placeholder="Select stages..." data-id="element-367" />

            <SingleSelectDropdown label="How do you primarily invest?" options={['Individual Angel', 'Venture Fund (GP)', 'Venture Fund (LP)', 'Syndicate Lead', 'Syndicate Member', 'Family Office', 'Corporate VC', 'Other']} selected={formData.howYouInvest} onChange={selected => setFormData({
            ...formData,
            howYouInvest: selected
          })} placeholder="Select type..." otherValue={formData.howYouInvestOther} onOtherChange={value => setFormData({
            ...formData,
            howYouInvestOther: value
          })} data-id="element-368" />

            <div data-id="element-369">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-370">
                What geographies or markets do you focus on?
              </label>
              <input type="text" value={formData.geographiesMarkets} onChange={e => setFormData({
              ...formData,
              geographiesMarkets: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="e.g., North America, Europe, Global..." data-id="element-371" />
            </div>

            <MultiSelectDropdown label="What industries or sectors do you primarily invest in? (Select all that apply)" options={['SaaS or Enterprise Software', 'Consumer Tech', 'Fintech', 'Healthcare or Biotech', 'E-commerce or D2C', 'Climate or Cleantech', 'AI or ML', 'Hardware', 'Marketplace', 'Other']} selected={formData.industries} onChange={selected => setFormData({
            ...formData,
            industries: selected
          })} placeholder="Select industries..." otherValue={formData.industriesOther} onOtherChange={value => setFormData({
            ...formData,
            industriesOther: value
          })} data-id="element-372" />
          </div>;
      case 'portfolio':
        return <div className="space-y-8" data-id="element-373">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-374">
              Portfolio Composition
            </h3>

            <SingleSelectDropdown label="Of your portfolio companies, approximately what percentage were founded by solo founders vs cofounder teams?" options={['100% solo founders', 'Mostly solo (75%+)', 'More solo than cofounder', 'Roughly 50-50 mix', 'More cofounder than solo', 'Mostly cofounder teams (75%+)', '100% cofounder teams']} selected={formData.soloVsCofounderPercentage} onChange={selected => setFormData({
            ...formData,
            soloVsCofounderPercentage: selected
          })} placeholder="Select distribution..." data-id="element-375" />

            <SingleSelectDropdown label="Do you have a personal preference for investing in solo founders or cofounder teams?" options={['Strongly prefer solo founders', 'Slightly prefer solo founders', 'No strong preference', 'Slightly prefer cofounder teams', 'Strongly prefer cofounder teams']} selected={formData.personalPreference} onChange={selected => setFormData({
            ...formData,
            personalPreference: selected
          })} placeholder="Select preference..." data-id="element-376" />

            <MultiSelectDropdown label="If you prefer cofounder teams, why? (Select all that apply)" options={['Balanced skill sets', 'Shared accountability', 'Diverse perspectives', 'More leadership bandwidth', 'Lower key-person risk', 'Lower burnout risk', 'Better decision-making', 'Investors expect it', 'Historical data supports it', 'Other']} selected={formData.preferCofounderReasons} onChange={selected => setFormData({
            ...formData,
            preferCofounderReasons: selected
          })} placeholder="Select reasons..." otherValue={formData.preferCofounderReasonsOther} onOtherChange={value => setFormData({
            ...formData,
            preferCofounderReasonsOther: value
          })} data-id="element-377" />

            <MultiSelectDropdown label="If you prefer solo founders, why? (Select all that apply)" options={['Faster decision-making', 'No cofounder conflict risk', 'Clearer accountability', 'Simpler cap table', 'Strong solo founders outperform', 'Easier to work with', 'Other']} selected={formData.preferSoloReasons} onChange={selected => setFormData({
            ...formData,
            preferSoloReasons: selected
          })} placeholder="Select reasons..." otherValue={formData.preferSoloReasonsOther} onOtherChange={value => setFormData({
            ...formData,
            preferSoloReasonsOther: value
          })} data-id="element-378" />

            <SingleSelectDropdown label="Have your views on solo founders vs cofounder teams changed over your investing career?" options={['Yes, I now prefer solo founders more', 'Yes, I now prefer cofounder teams more', "Yes, I've become more nuanced", 'No, my views have stayed consistent']} selected={formData.viewsChanged} onChange={selected => setFormData({
            ...formData,
            viewsChanged: selected
          })} placeholder="Select answer..." data-id="element-379" />

            {formData.viewsChanged.startsWith('Yes') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-380">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-381">
                  If your views have changed, what experiences or observations
                  drove that shift?
                </label>
                <textarea value={formData.viewsChangedExplanation} onChange={e => setFormData({
              ...formData,
              viewsChangedExplanation: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what changed your perspective..." data-id="element-382" />
              </motion.div>}
          </div>;
      case 'evaluating':
        return <div className="space-y-8" data-id="element-383">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-384">
              Evaluating Founding Teams
            </h3>

            <div data-id="element-385">
              <label className="block text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-386">
                When evaluating an early-stage investment, how do you weight:
              </label>
              <div className="space-y-4" data-id="element-387">
                {[{
                key: 'teamFoundersImportance',
                label: 'Team and Founders'
              }, {
                key: 'marketOpportunityImportance',
                label: 'Market opportunity'
              }, {
                key: 'productTechnologyImportance',
                label: 'Product or Technology'
              }, {
                key: 'tractionMetricsImportance',
                label: 'Traction or Metrics'
              }, {
                key: 'cofounderDynamicsImportance',
                label: 'Cofounder dynamics and alignment'
              }].map(({
                key,
                label
              }) => <div key={key} data-id="element-388">
                    <p className="text-[#3D5654] mb-2" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px'
                }} data-id="element-389">
                      {label}
                    </p>
                    <div className="flex gap-2" data-id="element-390">
                      {['Not important', 'Slightly important', 'Moderately important', 'Very important', 'Critical factor'].map(rating => <button key={rating} onClick={() => setFormData({
                    ...formData,
                    [key]: rating
                  })} className={`flex-1 py-2 rounded-lg border-2 transition-all text-xs ${formData[key as keyof SurveyData] === rating ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }} data-id="element-391">
                          {rating}
                        </button>)}
                    </div>
                  </div>)}
              </div>
            </div>

            <SingleSelectDropdown label="At what stage does founder alignment matter MOST in your investment decision?" options={["Pre-seed (it's everything early on)", 'Seed (top factor alongside product)', 'Series A (matters but execution matters more)', 'Series B+ (only if it affects performance)', "It's always equally important regardless of stage"]} selected={formData.alignmentMattersMost} onChange={selected => setFormData({
            ...formData,
            alignmentMattersMost: selected
          })} placeholder="Select stage..." data-id="element-392" />

            <SingleSelectDropdown label="How much time do you typically spend assessing cofounder dynamics during due diligence?" options={['Very little — I focus on other factors', 'Some time — a few targeted questions', "Moderate time — it's part of my standard process", 'Significant time — I dig deep into team dynamics', "It's one of my primary focus areas"]} selected={formData.timeAssessingDynamics} onChange={selected => setFormData({
            ...formData,
            timeAssessingDynamics: selected
          })} placeholder="Select answer..." data-id="element-393" />
          </div>;
      case 'signals':
        return <div className="space-y-8" data-id="element-394">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-395">
              Signals of Healthy Relationships
            </h3>

            <MultiSelectDropdown label="What signals indicate a HEALTHY cofounder relationship? (Select all that apply)" options={['Clear and open communication', 'Complementary skills and roles', 'Shared long-term vision', 'Handles disagreement constructively', 'Mutual respect and trust', 'Balanced decision-making', 'History of working together', 'Aligned on equity and compensation', 'Similar commitment levels', "Can articulate each other's strengths and weaknesses", "Finish each other's sentences", 'Comfortable disagreeing in front of others', 'Have explicit agreements in writing', 'Other']} selected={formData.healthySignals} onChange={selected => setFormData({
            ...formData,
            healthySignals: selected
          })} placeholder="Select signals..." otherValue={formData.healthySignalsOther} onOtherChange={value => setFormData({
            ...formData,
            healthySignalsOther: value
          })} data-id="element-396" />

            <div data-id="element-397">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-398">
                What is the SINGLE most important indicator of a healthy
                cofounder relationship?
              </label>
              <textarea value={formData.singleMostImportantIndicator} onChange={e => setFormData({
              ...formData,
              singleMostImportantIndicator: e.target.value
            })} rows={3} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your perspective..." data-id="element-399" />
            </div>

            <div data-id="element-400">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-401">
                What questions do you ask founders to assess trust,
                communication, or decision-making style?
              </label>
              <textarea value={formData.questionsToAssess} onChange={e => setFormData({
              ...formData,
              questionsToAssess: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the questions you ask..." data-id="element-402" />
            </div>

            <SingleSelectDropdown label="Do you observe how cofounders interact with each other during meetings?" options={["Always — it's essential", 'Usually — when I remember', 'Sometimes', 'Rarely', 'Never']} selected={formData.observeInteractions} onChange={selected => setFormData({
            ...formData,
            observeInteractions: selected
          })} placeholder="Select frequency..." data-id="element-403" />

            <MultiSelectDropdown label="What specific behaviors do you watch for when cofounders are together? (Select all that apply)" options={['How they handle disagreement', 'Who speaks more vs less', 'Eye contact and body language', 'Whether they interrupt each other', 'How they defer or yield', 'Whether they credit each other', "How they handle questions they can't answer", 'Energy and rapport between them', 'Other']} selected={formData.specificBehaviors} onChange={selected => setFormData({
            ...formData,
            specificBehaviors: selected
          })} placeholder="Select behaviors..." otherValue={formData.specificBehaviorsOther} onOtherChange={value => setFormData({
            ...formData,
            specificBehaviorsOther: value
          })} data-id="element-404" />

            <SingleSelectDropdown label="Have you ever changed your investment decision after observing how cofounders interact with each other?" options={['Yes, decided to invest when I was unsure', 'Yes, decided NOT to invest when I was leaning yes', 'Yes, both situations have happened', 'No, but observations have influenced my conviction level', "No, I don't factor this in"]} selected={formData.changedDecision} onChange={selected => setFormData({
            ...formData,
            changedDecision: selected
          })} placeholder="Select answer..." data-id="element-405" />

            {formData.changedDecision.startsWith('Yes') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-406">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-407">
                  Can you share an example of what you observed that changed
                  your decision?
                </label>
                <textarea value={formData.observationExample} onChange={e => setFormData({
              ...formData,
              observationExample: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what you observed..." data-id="element-408" />
              </motion.div>}
          </div>;
      case 'red-flags':
        return <div className="space-y-8" data-id="element-409">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-410">
              Red Flags and Warning Signs
            </h3>

            <MultiSelectDropdown label="What are the biggest RED FLAGS you look for in cofounder relationships? (Select all that apply)" options={['Misaligned vision or goals', 'Poor or strained communication', 'One founder clearly dominates', 'Unclear roles or responsibilities', 'Unequal commitment or effort', 'Equity disputes or resentment', 'Different risk tolerances', 'Conflicting timelines (one wants quick exit)', 'Lack of mutual respect', 'Avoiding difficult conversations', 'Badmouthing each other', 'Recent or unresolved conflict', 'No formal agreements in place', 'Met very recently', 'Romantic relationship', 'Family members with unresolved dynamics', 'Other']} selected={formData.redFlags} onChange={selected => setFormData({
            ...formData,
            redFlags: selected
          })} placeholder="Select red flags..." otherValue={formData.redFlagsOther} onOtherChange={value => setFormData({
            ...formData,
            redFlagsOther: value
          })} data-id="element-411" />

            <div data-id="element-412">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-413">
                What is the SINGLE biggest red flag that would make you pass on
                an investment?
              </label>
              <textarea value={formData.singleBiggestRedFlag} onChange={e => setFormData({
              ...formData,
              singleBiggestRedFlag: e.target.value
            })} rows={3} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the biggest red flag..." data-id="element-414" />
            </div>

            <SingleSelectDropdown label="Have you ever invested despite seeing red flags in cofounder dynamics?" options={['Yes, multiple times', 'Yes, once or twice', 'No, I always pass', "No, but I've come close"]} selected={formData.investedDespiteRedFlags} onChange={selected => setFormData({
            ...formData,
            investedDespiteRedFlags: selected
          })} placeholder="Select answer..." data-id="element-415" />

            {formData.investedDespiteRedFlags.startsWith('Yes') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-416">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-417">
                  If yes, what happened? Did the investment work out?
                </label>
                <textarea value={formData.investedDespiteRedFlagsOutcome} onChange={e => setFormData({
              ...formData,
              investedDespiteRedFlagsOutcome: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what happened..." data-id="element-418" />
              </motion.div>}

            <SingleSelectDropdown label="Have you ever avoided or withdrawn from an investment specifically due to cofounder conflict or misalignment?" options={['Yes, multiple times', 'Yes, once or twice', "No, but I've considered it", 'No, never']} selected={formData.avoidedDueToConflict} onChange={selected => setFormData({
            ...formData,
            avoidedDueToConflict: selected
          })} placeholder="Select answer..." data-id="element-419" />

            {formData.avoidedDueToConflict.startsWith('Yes') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-420">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-421">
                  Can you share what you observed that led you to pass or
                  withdraw?
                </label>
                <textarea value={formData.avoidedDueToConflictDetails} onChange={e => setFormData({
              ...formData,
              avoidedDueToConflictDetails: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what you observed..." data-id="element-422" />
              </motion.div>}
          </div>;
      case 'conflict':
        return <div className="space-y-8" data-id="element-423">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-424">
              Cofounder Conflict
            </h3>

            <SingleSelectDropdown label="In your portfolio, approximately what percentage of cofounder teams have experienced significant conflict?" options={['Less than 10%', '10–25%', '26–50%', '51–75%', 'More than 75%', 'Nearly all of them']} selected={formData.portfolioConflictPercentage} onChange={selected => setFormData({
            ...formData,
            portfolioConflictPercentage: selected
          })} placeholder="Select percentage..." data-id="element-425" />

            <MultiSelectDropdown label="What typically triggers cofounder conflict? (Select all that apply)" options={['Disagreement on company direction', 'Unequal workload or contribution', 'Compensation or equity disputes', 'Hiring or firing decisions', 'Fundraising strategy disagreements', "One founder's performance issues", 'External stress (financial or personal)', 'Success and scaling challenges', 'Board or investor pressure', 'Different visions for exit', 'Personal life changes (marriage, kids, health)', 'Bringing on new executives', 'Personality clashes that worsen over time', 'Other']} selected={formData.conflictTriggers} onChange={selected => setFormData({
            ...formData,
            conflictTriggers: selected
          })} placeholder="Select triggers..." otherValue={formData.conflictTriggersOther} onOtherChange={value => setFormData({
            ...formData,
            conflictTriggersOther: value
          })} data-id="element-426" />

            <SingleSelectDropdown label="At what company stage do cofounder conflicts most commonly emerge?" options={['Pre-seed or Formation', 'Seed (building MVP, first fundraise)', 'Series A (early scaling)', 'Series B+ (growth phase)', 'Pre-exit or acquisition', 'Conflicts happen at all stages equally']} selected={formData.conflictStage} onChange={selected => setFormData({
            ...formData,
            conflictStage: selected
          })} placeholder="Select stage..." data-id="element-427" />

            <SingleSelectDropdown label="How long can unresolved cofounder tension typically go before it starts affecting company performance?" options={['Immediately', 'Less than 1 month', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'It varies too much to say']} selected={formData.unresolvedTensionTimeline} onChange={selected => setFormData({
            ...formData,
            unresolvedTensionTimeline: selected
          })} placeholder="Select timeline..." data-id="element-428" />

            <SingleSelectDropdown label="When tensions emerge between cofounders, what is your usual approach as an investor?" options={["Completely hands-off — it's their issue to resolve", "Light-touch — I check in but don't intervene", 'Offer resources — suggest coaches or mediators', 'Active mediation — I help facilitate conversations', 'Board-level intervention — formal process', 'Push for leadership change if needed', 'It depends on the severity']} selected={formData.investorApproach} onChange={selected => setFormData({
            ...formData,
            investorApproach: selected
          })} placeholder="Select approach..." data-id="element-429" />

            <div data-id="element-430">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-431">
                What differentiates cofounder teams that RECOVER from conflict
                vs those that DON'T?
              </label>
              <textarea value={formData.recoverVsDontDifferentiator} onChange={e => setFormData({
              ...formData,
              recoverVsDontDifferentiator: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what makes the difference..." data-id="element-432" />
            </div>

            <MultiSelectDropdown label="What resources or interventions have you seen make the biggest difference in helping cofounders through conflict? (Select all that apply)" options={['Executive coach', 'Couples or team therapist', 'Board mentor or advisor', 'External facilitator or mediator', "Peer founder who's been through it", 'Formal conflict resolution process', 'Restructuring roles or responsibilities', 'Adjusting equity or compensation', 'Time and space apart', 'Nothing has worked well', 'Other']} selected={formData.helpfulResources} onChange={selected => setFormData({
            ...formData,
            helpfulResources: selected
          })} placeholder="Select resources..." otherValue={formData.helpfulResourcesOther} onOtherChange={value => setFormData({
            ...formData,
            helpfulResourcesOther: value
          })} data-id="element-433" />
          </div>;
      case 'splits':
        return <div className="space-y-8" data-id="element-434">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-435">
              Cofounder Splits and Departures
            </h3>

            <SingleSelectDropdown label="Roughly what percentage of your portfolio companies have experienced a cofounder split or major founder departure?" options={['Less than 10%', '10–25%', '26–40%', '41–50%', 'More than 50%']} selected={formData.cofounderSplitPercentage} onChange={selected => setFormData({
            ...formData,
            cofounderSplitPercentage: selected
          })} placeholder="Select percentage..." data-id="element-436" />

            <SingleSelectDropdown label="By what stage do most cofounder splits occur?" options={['Before any funding', 'Pre-seed to Seed', 'Seed to Series A', 'Series A to Series B', 'Series B or later', 'Pre-exit', 'Varies too much to generalize']} selected={formData.splitStage} onChange={selected => setFormData({
            ...formData,
            splitStage: selected
          })} placeholder="Select stage..." data-id="element-437" />

            <SingleSelectDropdown label="When a cofounder departs, what typically happens to the company?" options={['Company usually survives and thrives', 'Company survives but is weakened', 'Company often fails within 1–2 years', 'It completely depends on which founder leaves', 'It completely depends on how the departure is handled']} selected={formData.departureOutcome} onChange={selected => setFormData({
            ...formData,
            departureOutcome: selected
          })} placeholder="Select outcome..." data-id="element-438" />

            <MultiSelectDropdown label="What determines whether a company survives a cofounder departure? (Select all that apply)" options={['Which cofounder leaves', 'Stage of the company', 'How the departure is handled', "Whether it's amicable or contentious", "Remaining founder's strength", 'Investor support and involvement', 'Customer and employee retention', 'Clean legal and equity resolution', 'Speed of finding replacement leadership', 'Other']} selected={formData.survivalFactors} onChange={selected => setFormData({
            ...formData,
            survivalFactors: selected
          })} placeholder="Select factors..." otherValue={formData.survivalFactorsOther} onOtherChange={value => setFormData({
            ...formData,
            survivalFactorsOther: value
          })} data-id="element-439" />

            <div data-id="element-440">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-441">
                Can you share a story about a portfolio company that SURVIVED a
                cofounder split? What helped them pull through?
              </label>
              <textarea value={formData.survivedSplitStory} onChange={e => setFormData({
              ...formData,
              survivedSplitStory: e.target.value
            })} rows={5} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the story..." data-id="element-442" />
            </div>

            <div data-id="element-443">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-444">
                Can you share a story about a company that FAILED or struggled
                significantly after a cofounder split?
              </label>
              <textarea value={formData.failedSplitStory} onChange={e => setFormData({
              ...formData,
              failedSplitStory: e.target.value
            })} rows={5} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the story..." data-id="element-445" />
            </div>

            <SingleSelectDropdown label="Have you ever seen a cofounder dispute derail an exit or acquisition?" options={['Yes, multiple times', 'Yes, once or twice', "No, but I've seen it come close", 'No, never']} selected={formData.derailedExit} onChange={selected => setFormData({
            ...formData,
            derailedExit: selected
          })} placeholder="Select answer..." data-id="element-446" />

            {formData.derailedExit.startsWith('Yes') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-447">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-448">
                  If yes, what happened?
                </label>
                <textarea value={formData.derailedExitDetails} onChange={e => setFormData({
              ...formData,
              derailedExitDetails: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what happened..." data-id="element-449" />
              </motion.div>}

            <div data-id="element-450">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-451">
                On a scale of 1–5, how much do founder alignment issues
                typically affect valuation at exit?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-452">
                1 = No effect at all → 5 = Major impact on valuation
              </p>
              <div className="flex gap-4 justify-center" data-id="element-453">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                alignmentAffectValuation: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.alignmentAffectValuation === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-454">
                    {num}
                  </button>)}
              </div>
            </div>
          </div>;
      case 'predicting':
        return <div className="space-y-8" data-id="element-455">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-456">
              Predicting Long-Term Alignment
            </h3>

            <SingleSelectDropdown label="Do you believe the quality of cofounder alignment at exit can be predicted from early-stage dynamics?" options={['Yes — early signs are strong predictors', 'Somewhat — early clues help but things change', 'No — alignment evolves too much over time', 'Unsure']} selected={formData.predictFromEarly} onChange={selected => setFormData({
            ...formData,
            predictFromEarly: selected
          })} placeholder="Select answer..." data-id="element-457" />

            <MultiSelectDropdown label="What EARLY indicators suggest cofounders will stay aligned long-term? (Select all that apply)" options={['History of working together successfully', 'Complementary but not overlapping skills', 'Explicit agreements and documentation', 'Healthy conflict resolution patterns', 'Aligned on timeline and exit goals', 'Similar life stages and circumstances', 'Mutual respect visible in interactions', 'Both have skin in the game financially', 'Clear role definition from day one', 'Regular communication habits', 'Other']} selected={formData.earlyPositiveIndicators} onChange={selected => setFormData({
            ...formData,
            earlyPositiveIndicators: selected
          })} placeholder="Select indicators..." otherValue={formData.earlyPositiveIndicatorsOther} onOtherChange={value => setFormData({
            ...formData,
            earlyPositiveIndicatorsOther: value
          })} data-id="element-458" />

            <MultiSelectDropdown label="What EARLY indicators suggest cofounders may struggle long-term? (Select all that apply)" options={['Met very recently', 'Never worked together before', 'One founder is clearly dominant', 'Avoiding hard conversations', 'Vague or unequal equity split', 'Different definitions of success', 'Different risk tolerances', 'Significant life stage differences', 'One founder doing this on the side', 'History of broken partnerships', 'Romantic or family relationship', 'Other']} selected={formData.earlyNegativeIndicators} onChange={selected => setFormData({
            ...formData,
            earlyNegativeIndicators: selected
          })} placeholder="Select indicators..." otherValue={formData.earlyNegativeIndicatorsOther} onOtherChange={value => setFormData({
            ...formData,
            earlyNegativeIndicatorsOther: value
          })} data-id="element-459" />

            <div data-id="element-460">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-461">
                What is the most UNDERRATED factor that helps cofounders stay
                aligned long-term?
              </label>
              <textarea value={formData.underratedFactor} onChange={e => setFormData({
              ...formData,
              underratedFactor: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the underrated factor..." data-id="element-462" />
            </div>
          </div>;
      case 'solo':
        return <div className="space-y-8" data-id="element-463">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-464">
              Solo Founders
            </h3>

            <MultiSelectDropdown label="When you invest in solo founders, what qualities make you confident in their ability to succeed alone? (Select all that apply)" options={['Exceptional individual capability', 'Strong self-awareness', 'Proven track record', 'Ability to attract and retain talent', 'Robust advisory network', 'Coachability', 'Emotional resilience', "Clear on what they don't know", 'Strong board or support structure', 'Previous experience as a solo founder', 'Other']} selected={formData.soloFounderQualities} onChange={selected => setFormData({
            ...formData,
            soloFounderQualities: selected
          })} placeholder="Select qualities..." otherValue={formData.soloFounderQualitiesOther} onOtherChange={value => setFormData({
            ...formData,
            soloFounderQualitiesOther: value
          })} data-id="element-465" />

            <div data-id="element-466">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-467">
                What is the SINGLE most important quality for solo founder
                success?
              </label>
              <textarea value={formData.singleMostImportantQuality} onChange={e => setFormData({
              ...formData,
              singleMostImportantQuality: e.target.value
            })} rows={3} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the most important quality..." data-id="element-468" />
            </div>

            <MultiSelectDropdown label="What are the biggest risks you see with solo founders? (Select all that apply)" options={['Key-person risk', 'Burnout', 'Slower execution', 'Blind spots', 'Lack of accountability', 'Harder to fundraise', 'Weaker governance', 'Loneliness affecting judgment', 'Other']} selected={formData.soloFounderRisks} onChange={selected => setFormData({
            ...formData,
            soloFounderRisks: selected
          })} placeholder="Select risks..." otherValue={formData.soloFounderRisksOther} onOtherChange={value => setFormData({
            ...formData,
            soloFounderRisksOther: value
          })} data-id="element-469" />

            <div data-id="element-470">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-471">
                How do the best solo founders mitigate these risks?
              </label>
              <textarea value={formData.soloFounderMitigation} onChange={e => setFormData({
              ...formData,
              soloFounderMitigation: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share how they mitigate risks..." data-id="element-472" />
            </div>

            <SingleSelectDropdown label="Have you seen solo founders outperform cofounder teams in your portfolio?" options={['Yes, frequently', 'Yes, occasionally', 'Rarely', 'Never', 'Performance is roughly equal']} selected={formData.soloOutperformCofounder} onChange={selected => setFormData({
            ...formData,
            soloOutperformCofounder: selected
          })} placeholder="Select answer..." data-id="element-473" />

            <SingleSelectDropdown label="In your experience, are solo founders MORE or LESS likely to successfully exit compared to cofounder teams?" options={['Solo founders more likely to exit', 'Cofounder teams more likely to exit', 'About the same', 'Insufficient data to say', 'It depends on other factors more than team structure']} selected={formData.soloExitLikelihood} onChange={selected => setFormData({
            ...formData,
            soloExitLikelihood: selected
          })} placeholder="Select answer..." data-id="element-474" />
          </div>;
      case 'growth':
        return <div className="space-y-8" data-id="element-475">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-476">
              Founder Growth and Evolution
            </h3>

            <SingleSelectDropdown label="How often do you see cofounder dynamics IMPROVE over time?" options={['Rarely — dynamics tend to stay the same or worsen', 'Occasionally', 'Frequently', 'Almost always — founders grow together']} selected={formData.dynamicsImproveFrequency} onChange={selected => setFormData({
            ...formData,
            dynamicsImproveFrequency: selected
          })} placeholder="Select frequency..." data-id="element-477" />

            <MultiSelectDropdown label="What behaviors do you associate with cofounders who grow together successfully? (Select all that apply)" options={['Regular reflection and check-ins', 'Clear boundaries and role evolution', 'Shared decision-making rituals', 'Willingness to seek outside help', 'Ability to have hard conversations', "Celebrating each other's wins", 'Evolving the relationship as company grows', 'Maintaining friendship alongside partnership', 'Other']} selected={formData.growTogetherBehaviors} onChange={selected => setFormData({
            ...formData,
            growTogetherBehaviors: selected
          })} placeholder="Select behaviors..." otherValue={formData.growTogetherBehaviorsOther} onOtherChange={value => setFormData({
            ...formData,
            growTogetherBehaviorsOther: value
          })} data-id="element-478" />

            <SingleSelectDropdown label="Do you believe early cofounder misalignment can be corrected with the right support?" options={['Yes, often', 'Sometimes, with significant effort', 'Rarely — early dynamics are sticky', 'No — misalignment tends to worsen']} selected={formData.misalignmentCorrectable} onChange={selected => setFormData({
            ...formData,
            misalignmentCorrectable: selected
          })} placeholder="Select answer..." data-id="element-479" />

            <div data-id="element-480">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-481">
                How do you spot whether a founding team can grow into their next
                stage of leadership?
              </label>
              <textarea value={formData.spotGrowthPotential} onChange={e => setFormData({
              ...formData,
              spotGrowthPotential: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share how you spot growth potential..." data-id="element-482" />
            </div>
          </div>;
      case 'investor-role':
        return <div className="space-y-8" data-id="element-483">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-484">
              Investor's Role in Alignment
            </h3>

            <div data-id="element-485">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-486">
                How much responsibility do investors have in helping founders
                manage their cofounder dynamics?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-487">
                1 = No responsibility → 5 = Significant responsibility
              </p>
              <div className="flex gap-4 justify-center" data-id="element-488">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                investorResponsibility: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.investorResponsibility === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-489">
                    {num}
                  </button>)}
              </div>
            </div>

            <SingleSelectDropdown label="Do you believe investors can positively influence cofounder alignment through their own behavior?" options={['Yes, significantly', 'Yes, somewhat', 'Minimally', "No, it's up to the founders", 'It can actually make things worse']} selected={formData.investorInfluence} onChange={selected => setFormData({
            ...formData,
            investorInfluence: selected
          })} placeholder="Select answer..." data-id="element-490" />

            <MultiSelectDropdown label="What can investors do to support healthy cofounder dynamics? (Select all that apply)" options={['Model healthy communication', 'Ask about cofounder relationship in check-ins', 'Provide coaching resources', 'Facilitate difficult conversations', 'Set expectations early about conflict resolution', 'Connect founders with peer mentors', 'Stay neutral during disputes', 'Know when to step back', 'Other']} selected={formData.supportHealthyDynamics} onChange={selected => setFormData({
            ...formData,
            supportHealthyDynamics: selected
          })} placeholder="Select actions..." otherValue={formData.supportHealthyDynamicsOther} onOtherChange={value => setFormData({
            ...formData,
            supportHealthyDynamicsOther: value
          })} data-id="element-491" />

            <div data-id="element-492">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-493">
                What mistakes have you made (if any) in assessing or influencing
                founder alignment?
              </label>
              <textarea value={formData.investorMistakes} onChange={e => setFormData({
              ...formData,
              investorMistakes: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share any mistakes or lessons learned..." data-id="element-494" />
            </div>

            <div data-id="element-495">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-496">
                What do you wish more founders understood about how investors
                perceive cofounder dynamics?
              </label>
              <textarea value={formData.wishFoundersUnderstood} onChange={e => setFormData({
              ...formData,
              wishFoundersUnderstood: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what founders should know..." data-id="element-497" />
            </div>
          </div>;
      case 'future':
        return <div className="space-y-8" data-id="element-498">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-499">
              Future of Founder Fit Assessment
            </h3>

            <SingleSelectDropdown label="Do you believe founder fit — how well cofounders work together — will become a formal part of due diligence in the future?" options={['Yes, definitely', 'Probably', 'Possibly', 'Unlikely', 'No']} selected={formData.founderFitFormalDueDiligence} onChange={selected => setFormData({
            ...formData,
            founderFitFormalDueDiligence: selected
          })} placeholder="Select answer..." data-id="element-500" />

            <SingleSelectDropdown label="If a reliable CoFounder Fit assessment or score existed, would you use it as part of your investment process?" options={['Yes, definitely', 'Yes, probably', 'Maybe — depends on the methodology', 'Probably not', 'Definitely not']} selected={formData.useCofounderFitScore} onChange={selected => setFormData({
            ...formData,
            useCofounderFitScore: selected
          })} placeholder="Select answer..." data-id="element-501" />

            <div data-id="element-502">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-503">
                How much could a strong cofounder alignment score influence your
                confidence in investing?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-504">
                1 = Not at all → 5 = Significantly
              </p>
              <div className="flex gap-4 justify-center" data-id="element-505">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                alignmentScoreInfluence: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.alignmentScoreInfluence === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-506">
                    {num}
                  </button>)}
              </div>
            </div>

            <MultiSelectDropdown label="What would make a founder-assessment tool credible and valuable to you? (Select all that apply)" options={['Backed by data and research', 'Validated against actual outcomes', 'Used by respected investors', 'Recommended by founders themselves', 'Integrates into existing due diligence', 'Provides actionable insights', 'Tracks changes over time', 'Assesses both strengths and risks', 'Includes qualitative and quantitative elements', 'Endorsed by accelerators or VCs', 'Other']} selected={formData.toolCredibility} onChange={selected => setFormData({
            ...formData,
            toolCredibility: selected
          })} placeholder="Select factors..." otherValue={formData.toolCredibilityOther} onOtherChange={value => setFormData({
            ...formData,
            toolCredibilityOther: value
          })} data-id="element-507" />

            <MultiSelectDropdown label="What specific dimensions should such a tool assess? (Select all that apply)" options={['Communication patterns', 'Decision-making alignment', 'Vision and goal alignment', 'Role clarity', 'Trust levels', 'Conflict resolution style', 'Commitment levels', 'Values alignment', 'Working style compatibility', 'Equity and compensation satisfaction', 'Long-term timeline alignment', 'Other']} selected={formData.toolDimensions} onChange={selected => setFormData({
            ...formData,
            toolDimensions: selected
          })} placeholder="Select dimensions..." otherValue={formData.toolDimensionsOther} onOtherChange={value => setFormData({
            ...formData,
            toolDimensionsOther: value
          })} data-id="element-508" />

            <div data-id="element-509">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-510">
                How would you like to see founder alignment assessed in future
                investment rounds?
              </label>
              <textarea value={formData.futureAssessment} onChange={e => setFormData({
              ...formData,
              futureAssessment: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your vision for future assessment..." data-id="element-511" />
            </div>
          </div>;
      case 'stories':
        return <div className="space-y-8" data-id="element-512">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-513">
              Stories and Lessons Learned
            </h3>

            <div data-id="element-514">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-515">
                What is ONE story from your investing career that taught you the
                most about cofounder dynamics — good or bad?
              </label>
              <textarea value={formData.mostTeachingStory} onChange={e => setFormData({
              ...formData,
              mostTeachingStory: e.target.value
            })} rows={6} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the story and what you learned..." data-id="element-516" />
            </div>

            <div data-id="element-517">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-518">
                Can you share an example where STRONG cofounder dynamics made a
                measurable difference in a company's success?
              </label>
              <textarea value={formData.strongDynamicsExample} onChange={e => setFormData({
              ...formData,
              strongDynamicsExample: e.target.value
            })} rows={6} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the example..." data-id="element-519" />
            </div>

            <div data-id="element-520">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-521">
                Can you share an example where MISALIGNMENT or conflict hurt an
                otherwise promising investment?
              </label>
              <textarea value={formData.misalignmentExample} onChange={e => setFormData({
              ...formData,
              misalignmentExample: e.target.value
            })} rows={6} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share the example..." data-id="element-522" />
            </div>

            <div data-id="element-523">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-524">
                What is the best cofounder relationship you've ever seen in your
                portfolio? What made it work?
              </label>
              <textarea value={formData.bestCofounderRelationship} onChange={e => setFormData({
              ...formData,
              bestCofounderRelationship: e.target.value
            })} rows={6} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what made it exceptional..." data-id="element-525" />
            </div>

            <div data-id="element-526">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-527">
                What is the worst cofounder breakdown you've witnessed? What
                went wrong?
              </label>
              <textarea value={formData.worstCofounderBreakdown} onChange={e => setFormData({
              ...formData,
              worstCofounderBreakdown: e.target.value
            })} rows={6} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what went wrong..." data-id="element-528" />
            </div>
          </div>;
      case 'advice':
        return <div className="space-y-8" data-id="element-529">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-530">
              Advice for Founders and Investors
            </h3>

            <div data-id="element-531">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-532">
                If you could give ONE piece of advice to cofounders before they
                raise capital, what would it be?
              </label>
              <textarea value={formData.adviceToCofounders} onChange={e => setFormData({
              ...formData,
              adviceToCofounders: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your advice..." data-id="element-533" />
            </div>

            <div data-id="element-534">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-535">
                What advice do you give cofounder teams about sustaining trust
                through the exit process?
              </label>
              <textarea value={formData.adviceOnTrust} onChange={e => setFormData({
              ...formData,
              adviceOnTrust: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your advice..." data-id="element-536" />
            </div>

            <div data-id="element-537">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-538">
                If you could give a new investor ONE piece of advice about
                evaluating cofounder alignment, what would it be?
              </label>
              <textarea value={formData.adviceToNewInvestor} onChange={e => setFormData({
              ...formData,
              adviceToNewInvestor: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your advice..." data-id="element-539" />
            </div>

            <div data-id="element-540">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-541">
                What do you wish someone had told YOU earlier in your career
                about founder dynamics?
              </label>
              <textarea value={formData.wishSomeoneToldYou} onChange={e => setFormData({
              ...formData,
              wishSomeoneToldYou: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share what you wish you knew..." data-id="element-542" />
            </div>
          </div>;
      case 'final':
        return <div className="space-y-8" data-id="element-543">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-544">
              Final Thoughts
            </h3>

            <div data-id="element-545">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-546">
                Why does cofounder alignment ultimately matter (or not) to you
                as an investor?
              </label>
              <textarea value={formData.whyAlignmentMatters} onChange={e => setFormData({
              ...formData,
              whyAlignmentMatters: e.target.value
            })} rows={5} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your perspective..." data-id="element-547" />
            </div>

            <div data-id="element-548">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-549">
                Is there anything else you would like to share about cofounder
                dynamics, founder alignment, or how you evaluate founding teams?
              </label>
              <textarea value={formData.anythingElse} onChange={e => setFormData({
              ...formData,
              anythingElse: e.target.value
            })} rows={5} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share any additional thoughts..." data-id="element-550" />
            </div>

            <SingleSelectDropdown label="Would you be open to a follow-up conversation to share more detailed stories or insights for potential inclusion in a book?" options={['Yes', 'No', 'Maybe']} selected={formData.openToFollowUp} onChange={selected => setFormData({
            ...formData,
            openToFollowUp: selected
          })} placeholder="Select answer..." data-id="element-551" />

            <SingleSelectDropdown label="May we quote you (with attribution) in future publications or research?" options={['Yes, with my name', 'Yes, but anonymously', 'No']} selected={formData.mayQuote} onChange={selected => setFormData({
            ...formData,
            mayQuote: selected
          })} placeholder="Select answer..." data-id="element-552" />

            <div className="bg-[#F8FAF9] p-6 rounded-lg" data-id="element-553">
              <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.7
            }} data-id="element-554">
                Thank you for completing this survey! Your insights will help
                founders build stronger partnerships and help investors better
                assess founding teams.
              </p>
            </div>
          </div>;
      default:
        return <div data-id="element-555">Section coming soon...</div>;
    }
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto" data-id="element-556">
      <div className="min-h-screen flex items-start justify-center p-4 py-8" data-id="element-557">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-[16px] w-full max-w-4xl relative" data-id="element-558">
          <div className="p-6 border-b border-[#1A3D3A]/10" data-id="element-559">
            <div className="flex justify-between items-start mb-4" data-id="element-560">
              <div data-id="element-561">
                <h2 className="text-[#1A3D3A] mb-2" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '28px',
                fontWeight: 500
              }} data-id="element-562">
                  Investor & Accelerator Research
                </h2>
                <p className="text-[#3D5654] text-sm" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} data-id="element-563">
                  {completedSections.size} of {SECTIONS.length} sections
                  completed
                </p>
              </div>
              <button onClick={onClose} className="text-[#3D5654] hover:text-[#1A3D3A] transition-colors" data-id="element-564">
                <X className="w-6 h-6" data-id="element-565" />
              </button>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-566">
              {SECTIONS.map(section => <button key={section.id} onClick={() => handleSectionChange(section.id)} className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm transition-all flex items-center gap-2 ${currentSection === section.id ? 'bg-[#1A3D3A] text-white' : completedSections.has(section.id) ? 'bg-[#C5F467] text-[#1A3D3A]' : 'bg-[#F8FAF9] text-[#3D5654] hover:bg-[#E8EAE9]'}`} style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500
            }} data-id="element-567">
                  {completedSections.has(section.id) && <Check className="w-4 h-4" data-id="element-568" />}
                  {section.label}
                </button>)}
            </div>
          </div>

          <div className="p-8" data-id="element-569">
            <AnimatePresence mode="wait" data-id="element-570">
              <motion.div key={currentSection} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} data-id="element-571">
                {renderSection()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="p-6 border-t border-[#1A3D3A]/10 flex justify-between items-center" data-id="element-572">
            <div className="text-sm text-[#3D5654]" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-573">
              {SECTIONS.find(s => s.id === currentSection)?.required && <span data-id="element-574">* Required section</span>}
              {isSaving && <span className="text-[#1A3D3A] ml-4" data-id="element-575">💾 Saving...</span>}
            </div>
            <button onClick={currentSection === 'final' ? handleSubmit : goToNextSection} disabled={isSubmitting || isSaving} className="flex items-center gap-2 px-8 py-3 bg-[#1A3D3A] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 600
          }} data-id="element-576">
              {isSubmitting ? 'Submitting...' : isSaving ? 'Saving...' : currentSection === 'final' ? 'Submit Survey' : <>
                  Continue
                  <ChevronRight className="w-4 h-4" data-id="element-577" />
                </>}
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}